import type {
  AuthSession,
  CompanySignup,
  DriverSignup,
  LoginCredentials,
  UserRole,
} from "../types/vtc";

import { api } from "./api";
import { getLang } from "../i18n";

const STORAGE_KEY = "vtc.session";

/** What the backend's /auth/{role}/signup and /auth/{role}/login return. */
interface AuthResponse {
  role: UserRole;
  user: {
    email: string;
    name?: string;
    firstName?: string;
    verification?: AuthSession["verification"];
  };
  accessToken: string;
  refreshToken: string;
}

const toSession = ({ role, user, accessToken, refreshToken }: AuthResponse): AuthSession => ({
  token: accessToken,
  refreshToken,
  role,
  email: user.email,
  name: user.name ?? user.firstName ?? user.email.split("@")[0],
  ...(user.verification && { verification: user.verification }),
});

/** POST /auth/company/login or /auth/driver/login */
export async function login({
  role,
  email,
  password,
}: LoginCredentials): Promise<AuthSession> {
  const { data } = await api.post<AuthResponse>(`/auth/${role}/login`, {
    email,
    password,
  });
  return toSession(data);
}

/**
 * POST /auth/driver/signup — creates the account and e-mails a 6-digit code;
 * the driver is logged in by verifyEmailCode. The form's "state" is the backend's "region".
 */
export async function registerDriver(
  { state, ...fields }: DriverSignup,
  turnstileToken?: string | null,
): Promise<{ email: string; message: string }> {
  const { data } = await api.post<{ email: string; message: string }>("/auth/driver/signup", {
    ...fields,
    // Cloudflare Turnstile token, checked by the backend (when enabled)
    ...(turnstileToken && { turnstileToken }),
    region: state,
    // E-mails (like the code) are sent in this language
    language: getLang(),
  });
  return data;
}

/** POST /auth/{role}/verify-email — checks the e-mailed code and logs the account in. */
export async function verifyEmailCode(
  role: UserRole,
  email: string,
  code: string,
): Promise<{ session: AuthSession; message: string }> {
  const { data } = await api.post<AuthResponse & { message: string }>(`/auth/${role}/verify-email`, { email, code });
  return { session: toSession(data), message: data.message };
}

/** POST /auth/{role}/resend-code */
export async function resendEmailCode(role: UserRole, email: string): Promise<string> {
  const { data } = await api.post<{ message: string }>(`/auth/${role}/resend-code`, { email });
  return data.message;
}

/**
 * POST /auth/company/signup — creates the account and e-mails a 6-digit code;
 * the account is logged in by verifyEmailCode. Company details (SIRET, address)
 * and the verification documents are added later, from the account.
 */
export async function registerCompany(
  { companyName, ...fields }: CompanySignup,
  turnstileToken?: string | null,
): Promise<{ email: string; message: string }> {
  const isCompany = fields.ownerType === "company";
  const { data } = await api.post<{ email: string; message: string }>("/auth/company/signup", {
    ...fields,
    ...(turnstileToken && { turnstileToken }),
    // Individuals: the backend uses their full name and "individual-rental".
    ...(isCompany ? { name: companyName.trim() } : { businessType: undefined }),
    // E-mails (like the code) are sent in this language
    language: getLang(),
  });
  return data;
}

/**
 * POST /auth/{role}/forgot-password — the backend always answers the same way,
 * so emails can't be probed. The e-mailed link opens /reset-password.
 */
export async function requestPasswordReset({
  email,
  role,
}: {
  email: string;
  role: UserRole;
}): Promise<string> {
  const { data } = await api.post<{ message: string }>(`/auth/${role}/forgot-password`, { email });
  return data.message;
}

/** POST /auth/{role}/reset-password with the token from the e-mailed link; signs out every session. */
export async function resetPassword({
  role,
  token,
  password,
  confirmPassword,
}: {
  role: UserRole;
  token: string;
  password: string;
  confirmPassword: string;
}): Promise<string> {
  const { data } = await api.post<{ message: string }>(`/auth/${role}/reset-password`, {
    token,
    password,
    confirmPassword,
  });
  return data.message;
}

/** POST /auth/change-email — e-mails a code to the new address (needs the current password). */
export async function requestEmailChange(newEmail: string, password: string): Promise<string> {
  const { data } = await api.post<{ message: string }>("/auth/change-email", { newEmail, password });
  return data.message;
}

/** POST /auth/change-email/confirm — switches to the new address; returns it. */
export async function confirmEmailChange(code: string): Promise<{ email: string; message: string }> {
  const { data } = await api.post<{ message: string; user: { email: string } }>("/auth/change-email/confirm", { code });
  updateSession({ email: data.user.email });
  return { email: data.user.email, message: data.message };
}

/** Persists the session — localStorage if "remember me", else this tab only. */
export function saveSession(session: AuthSession, remember: boolean): void {
  clearSession();
  const store = remember ? localStorage : sessionStorage;
  store.setItem(STORAGE_KEY, JSON.stringify(session));
}

/** Merges changes (e.g. refreshed tokens) into the stored session, keeping where it lives. */
export function updateSession(changes: Partial<AuthSession>): void {
  const store = localStorage.getItem(STORAGE_KEY) ? localStorage : sessionStorage;
  const session = getSession();
  if (session) store.setItem(STORAGE_KEY, JSON.stringify({ ...session, ...changes }));
}

export function getSession(): AuthSession | null {
  const raw =
    localStorage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(STORAGE_KEY);
  return raw ? (JSON.parse(raw) as AuthSession) : null;
}

/** POST /auth/logout — revokes this device's refresh token, then forgets the session. */
export async function logout(): Promise<void> {
  const refreshToken = getSession()?.refreshToken;
  // Stop this browser's push notifications while the token still works
  // (loaded lazily: push.ts depends on the API client, which depends on this file)
  await import("./push").then((m) => m.disablePush()).catch(() => {});
  try {
    if (refreshToken) await api.post("/auth/logout", { refreshToken });
  } catch {
    // Logging out locally still works if the call fails (offline, token already gone).
  } finally {
    clearSession();
  }
}

export function clearSession(): void {
  localStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(STORAGE_KEY);
}
